import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import StageCard from "@/components/StageCard";
import Gallery from "@/components/Gallery";
import { Reveal, Overline } from "@/components/motion";
import { getStages, getGallery } from "@/lib/api";
import { IMAGES } from "@/data/demo";

const StagePayet = () => {
  const [stages, setStages] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    getStages("stage_payet").then(setStages);
    getGallery("stage_payet").then(setGallery);
  }, []);

  const now = new Date();
  const upcoming = stages.filter((s) => !s.end_date || new Date(s.end_date) >= now);
  const past = stages.filter((s) => s.end_date && new Date(s.end_date) < now);

  return (
    <div data-testid="stage-payet-page">
      <PageHero
        testId="stage-payet-hero"
        contentKey="stagepayet"
        image="/stage-payet-hero.webp"
        overline="Stages de football — U8 à U13"
        titleLines={[
          <span key="a" className="text-white">STAGE</span>,
          <span key="b" className="text-gold">PAYET</span>,
        ]}
        subtitle="Des stages de perfectionnement haut de gamme sur la Côte d'Azur : éducateurs diplômés, groupes réduits, progression individuelle et expérience inoubliable pour les jeunes joueurs."
      />

      {/* STAGES */}
      <section data-testid="stages-section" className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <Reveal>
          <div>
            <Overline>Prochaines sessions</Overline>
            <h2 className="mt-4 font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tight">
              Les <span className="text-gold">stages</span>
            </h2>
          </div>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          {upcoming.map((stage, i) => (
            <Reveal key={stage.id} delay={i * 0.08} y={30}>
              <StageCard stage={stage} />
            </Reveal>
          ))}
        </div>
        {upcoming.length === 0 && (
          <p className="mt-14 text-white/50">Aucune session programmée pour le moment — revenez très vite.</p>
        )}
        {past.length > 0 && (
          <>
            <Reveal>
              <h3 className="mt-20 font-display uppercase text-3xl tracking-wide text-white/50">
                Stages passés
              </h3>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {past.map((stage) => (
                <StageCard key={stage.id} stage={stage} muted />
              ))}
            </div>
          </>
        )}
        <Reveal delay={0.1}>
          <p className="mt-10 text-white/40 text-sm max-w-2xl">
            Les places sont confirmées uniquement après paiement sécurisé Stripe. Tant que le paiement
            n'est pas validé, aucune place n'est débitée du stage.
          </p>
        </Reveal>
      </section>

      {/* GALERIE */}
      <div className="bg-[#0a0a0a] border-y border-white/5">
        <Gallery items={gallery} title="Nos anciens stages" overline="Souvenirs & émotions" testId="gallery-anciens-stages" />
      </div>

      {/* TENUES STAGE */}
      <section data-testid="tenues-section" className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-6">
            <Overline>L'équipement officiel</Overline>
            <h2 className="mt-4 font-display uppercase text-5xl sm:text-6xl leading-[0.88] tracking-tight">
              Tenues <span className="text-gold">Stage</span>
            </h2>
            <p className="mt-6 text-white/65 leading-relaxed max-w-lg">
              Maillots, shorts, chaussettes et accessoires aux couleurs noir et or de 3M Match Pro.
              Chaque stagiaire repart avec sa tenue officielle.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="relative overflow-hidden aspect-[4/5] max-w-md mx-auto group">
              <img
                src="/pack-joueurs.jpeg"
                alt="Pack joueurs officiel du stage Dimitri Payet"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default StagePayet;
